import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, ZoomControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "../../../services/api";
import { useAppSelector } from "../../../store/hooks";
import { obterIdTenent } from "../../../services/seguranca";
import { LocaSmartLayout } from "../../../components/locasmart-layout";
import { toast } from "react-toastify";
import { FaSyncAlt, FaLock, FaMapMarkerAlt, FaStreetView, FaSearch } from "react-icons/fa";
import { MeusVeiculos, VeiculoLista, semSinalHa24h } from "./MeusVeiculos";

interface VeiculoMapa {
    placa: string;
    marca: string;
    modelo: string;
    latitude: number;
    longitude: number;
    velocidade: number;
    odometroKm: number;
    status: string;
    ultimaAtualizacao: string;
    ignition: boolean;
    tipoVeiculo: number; // 1=Carro, 2=Moto, 4=Caminhão
    nomeLocatario?: string;
}

// Fix para ícones do Leaflet com webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const vehicleSvgs: Record<number, string> = {
    // Carro (TipoVeiculo.Carro = 1)
    1: `<path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>`,
    // Moto (TipoVeiculo.Moto = 2)
    2: `<path d="M19.44 9.03L15.41 5H11v2h3.59l2 2H5c-2.8 0-5 2.2-5 5s2.2 5 5 5c2.46 0 4.45-1.69 4.9-4h1.65l2.77-2.77c-.21.54-.32 1.14-.32 1.77 0 2.8 2.2 5 5 5s5-2.2 5-5c0-2.65-1.97-4.77-4.56-4.97zM7.82 15C7.4 16.15 6.28 17 5 17c-1.63 0-3-1.37-3-3s1.37-3 3-3c1.28 0 2.4.85 2.82 2H5v2h2.82zM19 17c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>`,
    // Caminhão (TipoVeiculo.Caminhão = 4)
    4: `<path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>`,
};

const createVehicleIcon = (ignition: boolean, velocidade: number, tipoVeiculo: number) => {
    const color = velocidade > 0 ? "#4CAF50" : ignition ? "#FFC107" : "#9E9E9E";
    const svgPath = vehicleSvgs[tipoVeiculo] || vehicleSvgs[1];

    return L.divIcon({
        className: "",
        iconSize: [32, 42],
        iconAnchor: [16, 42],
        popupAnchor: [0, -42],
        html: `
            <div style="position:relative;display:flex;flex-direction:column;align-items:center;">
                <div style="width:32px;height:32px;background:${color};border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="white">${svgPath}</svg>
                </div>
                <div style="width:8px;height:8px;background:${color};transform:rotate(45deg);margin-top:-5px;box-shadow:0 2px 4px rgba(0,0,0,0.2);"></div>
            </div>
        `,
    });
};

// Reenquadra o mapa para os veículos EXIBIDOS, mas apenas quando o conjunto muda (chaveFit) —
// nunca a cada atualização automática de posição (60s), para não "puxar" a câmera enquanto o
// usuário observa. Ao fixar uma única placa, aproxima mais (maxZoom 16).
function FitBounds({ veiculos, chaveFit }: { veiculos: VeiculoMapa[]; chaveFit: string }) {
    const map = useMap();
    const ultimaChave = useRef<string | null>(null);

    useEffect(() => {
        if (veiculos.length === 0) return;
        if (ultimaChave.current === chaveFit) return;
        const bounds = L.latLngBounds(veiculos.map(v => [v.latitude, v.longitude]));
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: veiculos.length === 1 ? 16 : 15 });
        ultimaChave.current = chaveFit;
    }, [veiculos, map, chaveFit]);

    return null;
}

function MapaVeiculos() {
    const token = useAppSelector(state => state.token);
    const [aba, setAba] = useState<'mapa' | 'lista'>('mapa');
    const [listaVeiculos, setListaVeiculos] = useState<VeiculoLista[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [busca, setBusca] = useState("");
    const [placasFixadas, setPlacasFixadas] = useState<Set<string>>(new Set());
    const [integradoRastreador, setIntegradoRastreador] = useState<boolean | null>(null);
    const [ultimaConsulta, setUltimaConsulta] = useState<Date | null>(null);
    const [enderecos, setEnderecos] = useState<Record<string, string>>({});
    const [carregandoEndereco, setCarregandoEndereco] = useState<Record<string, boolean>>({});
    const enderecosRef = useRef<Record<string, string>>({});

    const buscarEndereco = useCallback(async (placa: string, lat: number, lng: number) => {
        const cacheKey = `${placa}_${lat.toFixed(4)}_${lng.toFixed(4)}`;
        if (enderecosRef.current[cacheKey]) {
            if (enderecosRef.current[cacheKey] !== "loading") {
                setEnderecos(prev => ({ ...prev, [placa]: enderecosRef.current[cacheKey] }));
            }
            return;
        }
        enderecosRef.current[cacheKey] = "loading";
        setCarregandoEndereco(prev => ({ ...prev, [placa]: true }));
        try {
            const resp = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=pt-BR`);
            const data = await resp.json();
            let endereco = `${lat}, ${lng}`;
            if (data?.address) {
                const a = data.address;
                const parts = [a.road, a.house_number, a.suburb || a.neighbourhood, a.city || a.town || a.village, a.state].filter(Boolean);
                endereco = parts.join(', ') || data.display_name || endereco;
            }
            enderecosRef.current[cacheKey] = endereco;
            setEnderecos(prev => ({ ...prev, [placa]: endereco }));
        } catch {
            const fallback = `${lat}, ${lng}`;
            enderecosRef.current[cacheKey] = fallback;
            setEnderecos(prev => ({ ...prev, [placa]: fallback }));
        } finally {
            setCarregandoEndereco(prev => ({ ...prev, [placa]: false }));
        }
    }, []);

    const bloquearDesbloquearVeiculo = useCallback(async (placa: string) => {
        const acao = await new Promise<string | null>((resolve) => {
            const modal = document.createElement('div');
            modal.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;';

            const box = document.createElement('div');
            box.style.cssText = 'background:white;border-radius:12px;padding:28px;width:90%;max-width:400px;';
            box.innerHTML = `
                <h3 style="margin:0 0 8px 0;color:#1F467E;font-size:16px;">Bloqueio de Veículo</h3>
                <p style="margin:0 0 16px 0;color:#666;font-size:14px;">Placa: <strong id="placa-label"></strong></p>
                <p style="margin:0 0 16px 0;color:#666;font-size:13px;">Digite sua senha para confirmar a ação:</p>
                <input id="senha-bloqueio" type="password" placeholder="Sua senha" style="width:100%;padding:10px;border:1px solid #ddd;border-radius:6px;margin-bottom:16px;font-size:14px;box-sizing:border-box;" />
                <div id="erro-senha" style="color:#e53935;font-size:13px;margin-bottom:12px;display:none;">Senha incorreta</div>
                <div style="display:flex;gap:10px;justify-content:flex-end;">
                    <button id="btn-cancelar" style="padding:10px 20px;border:1px solid #ddd;border-radius:8px;cursor:pointer;background:#f8f9fa;font-size:14px;">Cancelar</button>
                    <button id="btn-bloquear" style="padding:10px 20px;border:none;border-radius:8px;cursor:pointer;background:#e53935;color:white;font-weight:600;font-size:14px;">Bloquear</button>
                    <button id="btn-desbloquear" style="padding:10px 20px;border:none;border-radius:8px;cursor:pointer;background:#4CAF50;color:white;font-weight:600;font-size:14px;">Desbloquear</button>
                </div>
            `;
            modal.appendChild(box);
            document.body.appendChild(modal);

            const placaLabel = box.querySelector('#placa-label');
            if (placaLabel) placaLabel.textContent = placa;

            const inputSenha = box.querySelector('#senha-bloqueio') as HTMLInputElement;
            inputSenha?.focus();

            const cleanup = () => { document.body.removeChild(modal); };

            box.querySelector('#btn-cancelar')?.addEventListener('click', () => { cleanup(); resolve(null); });
            modal.addEventListener('click', (e) => { if (e.target === modal) { cleanup(); resolve(null); } });

            const executar = async (tipo: string) => {
                const senha = inputSenha?.value;
                if (!senha) { inputSenha?.focus(); return; }

                const erroDiv = box.querySelector('#erro-senha') as HTMLElement;

                try {
                    const idEmpresa = obterIdTenent(token);
                    const placaEncoded = encodeURIComponent(placa.replace('-', '').toUpperCase());
                    const endpoint = tipo === 'bloquear' ? 'Bloquear' : 'Desbloquear';

                    await api.post(`/AzulMonitor/${endpoint}/${idEmpresa}/${placaEncoded}`, { senha });
                    cleanup();
                    resolve(tipo);
                } catch (error: any) {
                    const data = error.response?.data;
                    const msg = data?.Mensagem || data?.mensagem || data?.message || data?.title || (typeof data === 'string' ? data : null) || 'Erro ao enviar comando';
                    if (msg.toLowerCase().includes('senha')) {
                        if (erroDiv) { erroDiv.style.display = 'block'; erroDiv.textContent = 'Senha incorreta'; }
                        inputSenha.value = '';
                        inputSenha.focus();
                    } else {
                        cleanup();
                        toast.error(msg);
                        resolve(null);
                    }
                }
            };

            box.querySelector('#btn-bloquear')?.addEventListener('click', () => executar('bloquear'));
            box.querySelector('#btn-desbloquear')?.addEventListener('click', () => executar('desbloquear'));
            inputSenha?.addEventListener('keydown', (e) => { if (e.key === 'Escape') { cleanup(); resolve(null); } });
        });

        if (acao === 'bloquear') {
            toast.success('Comando de bloqueio enviado com sucesso!');
        } else if (acao === 'desbloquear') {
            toast.success('Comando de desbloqueio enviado com sucesso!');
        }
    }, [token]);

    const carregarVeiculos = useCallback(async () => {
        setCarregando(true);
        try {
            // Fonte única para as duas abas: /Lista inclui também os veículos sem posição
            // ("sem sinal"), que a subaba Meus Veículos precisa e o mapa filtra localmente.
            const response = await api.get(`/AzulMonitor/Lista/${obterIdTenent(token)}`);
            setListaVeiculos(response.data || []);
            setUltimaConsulta(new Date());
        } catch {
            toast.error("Não foi possível carregar os veículos no mapa.");
        } finally {
            setCarregando(false);
        }
    }, [token]);

    // Marcadores do mapa: só quem tem posição conhecida.
    const veiculos = useMemo<VeiculoMapa[]>(() => listaVeiculos
        .filter(v => v.possuiPosicao && v.latitude != null && v.longitude != null)
        .map(v => ({
            placa: v.placa,
            marca: v.marca,
            modelo: v.modelo,
            latitude: v.latitude!,
            longitude: v.longitude!,
            velocidade: v.velocidade ?? 0,
            odometroKm: v.odometroKm ?? 0,
            status: v.status,
            ultimaAtualizacao: v.ultimaAtualizacao ?? "",
            ignition: v.ignicao ?? false,
            tipoVeiculo: v.tipoVeiculo,
            nomeLocatario: v.nomeLocatario ?? undefined,
        })), [listaVeiculos]);

    const alertas24h = useMemo(() => listaVeiculos.filter(semSinalHa24h).length, [listaVeiculos]);

    useEffect(() => {
        const verificarIntegracaoECarregar = async () => {
            try {
                const configRes = await api.get('ConfiguracaoFilial/ResolvidaAtual');
                const possuiIntegracao = !!(configRes.data?.azulMonitorConfigurado);
                setIntegradoRastreador(possuiIntegracao);
                if (!possuiIntegracao) {
                    setCarregando(false);
                    return;
                }
                carregarVeiculos();
            } catch {
                setIntegradoRastreador(false);
                setCarregando(false);
            }
        };
        verificarIntegracaoECarregar();
        const interval = setInterval(carregarVeiculos, 60000);
        return () => clearInterval(interval);
    }, [carregarVeiculos]);

    const formatarData = (data: string) => {
        // new Date("") não lança exceção: retorna Invalid Date, que viraria o texto literal no popup.
        if (!data) return "—";
        const d = new Date(data);
        return isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-BR");
    };

    // Sem placas fixadas → mostra todos (comportamento original). Com fixadas → só as escolhidas.
    const veiculosExibidos = useMemo(
        () => placasFixadas.size === 0 ? veiculos : veiculos.filter(v => placasFixadas.has(v.placa)),
        [veiculos, placasFixadas]
    );

    // Resultados do autocomplete: casa por placa OU nome do locatário, ignora já fixadas, limita a 8.
    const resultadosBusca = useMemo(() => {
        const q = busca.trim().toLowerCase();
        if (!q) return [];
        return veiculos
            .filter(v => !placasFixadas.has(v.placa) &&
                (v.placa.toLowerCase().includes(q) || (v.nomeLocatario || "").toLowerCase().includes(q)))
            .slice(0, 8);
    }, [busca, veiculos, placasFixadas]);

    // Muda só quando o CONJUNTO de placas fixadas muda (não nas atualizações de posição) → dispara refit.
    const chaveFit = useMemo(
        () => placasFixadas.size ? Array.from(placasFixadas).sort().join(",") : "__todos__",
        [placasFixadas]
    );

    const fixarPlaca = (placa: string) => {
        setPlacasFixadas(prev => new Set(prev).add(placa));
        setBusca("");
    };
    const removerPlaca = (placa: string) => {
        setPlacasFixadas(prev => { const proximo = new Set(prev); proximo.delete(placa); return proximo; });
    };

    if (integradoRastreador === false) {
        return (
            <LocaSmartLayout>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: "16px", textAlign: "center", padding: "0 16px" }}>
                    <FaMapMarkerAlt size={64} color="#bdbdbd" />
                    <h2 style={{ margin: 0, color: "#616161", fontWeight: 600 }}>Mapa de Veículos</h2>
                    <p style={{ color: "#757575", maxWidth: 480, margin: 0 }}>
                        Necessária integração com a nossa Plataforma de Rastreio para utilizar o Mapa de Veículos.
                    </p>
                    <p style={{ color: "#9e9e9e", fontSize: "14px", margin: 0 }}>
                        Configure a integração em <strong>Minha Conta &gt; Integrações &gt; Agillock</strong>.
                    </p>
                </div>
            </LocaSmartLayout>
        );
    }

    return (
        <LocaSmartLayout>
            <div style={{ height: "calc(100vh - 80px)", display: "flex", flexDirection: "column" }}>
                <style>{`
                    @keyframes mapaBarraIndeterminada {
                        0%   { left: -35%; width: 35%; }
                        50%  { width: 55%; }
                        100% { left: 100%; width: 35%; }
                    }
                    @keyframes mapaGirar { to { transform: rotate(360deg); } }
                `}</style>

                {/* Subabas: Mapa | Meus Veículos */}
                <div style={{
                    display: "flex", gap: 0, background: "white",
                    borderBottom: "2px solid #e2e8f0", padding: "0 20px",
                }}>
                    {([['mapa', 'Mapa'], ['lista', 'Meus Veículos']] as const).map(([chave, titulo]) => (
                        <button
                            key={chave}
                            onClick={() => setAba(chave)}
                            style={{
                                padding: "10px 22px", border: "none", background: "none",
                                fontSize: "14px", cursor: "pointer", whiteSpace: "nowrap",
                                fontWeight: aba === chave ? 600 : 400,
                                color: aba === chave ? "#1F467E" : "#64748b",
                                borderBottom: aba === chave ? "2px solid #1F467E" : "2px solid transparent",
                                marginBottom: "-2px", transition: "color 0.15s",
                                display: "inline-flex", alignItems: "center", gap: "7px",
                            }}
                        >
                            {titulo}
                            {chave === 'lista' && alertas24h > 0 && (
                                <span
                                    title={`${alertas24h} veículo(s) sem sinal há mais de 24h`}
                                    style={{
                                        background: "#dc2626", color: "#fff", borderRadius: "999px",
                                        fontSize: "11px", fontWeight: 700, padding: "1px 7px",
                                        lineHeight: "16px", minWidth: "18px",
                                    }}
                                >
                                    {alertas24h}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {aba === 'lista' ? (
                    <MeusVeiculos
                        veiculos={listaVeiculos}
                        carregando={carregando}
                        ultimaConsulta={ultimaConsulta}
                        onAtualizar={carregarVeiculos}
                    />
                ) : (
                <>
                {/* Header */}
                <div style={{
                    padding: "12px 20px",
                    background: "white",
                    borderBottom: "1px solid #e0e0e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <h2 style={{ margin: 0, fontSize: "16px", color: "#1F467E", fontWeight: 600 }}>
                            Mapa de Veículos
                        </h2>
                        <span style={{
                            background: "#EBF0F7", color: "#1F467E", padding: "2px 10px",
                            borderRadius: "12px", fontSize: "13px", fontWeight: 500,
                        }}>
                            {placasFixadas.size > 0
                                ? `${veiculosExibidos.length} de ${veiculos.length} veículo(s)`
                                : `${veiculos.length} veículo(s)`}
                        </span>

                        {/* Busca (autocomplete) ao lado do título — não ocupa o mapa nem uma linha própria */}
                        <div style={{ position: "relative", width: "260px", maxWidth: "40vw" }}>
                            <FaSearch size={13} color="#8a94a6" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                            <input
                                value={busca}
                                onChange={(e) => setBusca(e.target.value)}
                                placeholder="Buscar placa ou locatário"
                                style={{
                                    width: "100%", padding: "8px 12px 8px 34px", fontSize: "13px",
                                    border: "1px solid #d5d9e0", borderRadius: "8px", boxSizing: "border-box",
                                    outline: "none", background: "white",
                                }}
                            />
                            {resultadosBusca.length > 0 && (
                                <div style={{
                                    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                                    background: "white", border: "1px solid #e0e0e0", borderRadius: "8px",
                                    boxShadow: "0 6px 20px rgba(0,0,0,0.18)", zIndex: 1250, overflow: "hidden",
                                    maxHeight: "300px", overflowY: "auto",
                                }}>
                                    {resultadosBusca.map((v) => (
                                        <div
                                            key={v.placa}
                                            // onMouseDown dispara ANTES do blur do input, garantindo a seleção
                                            onMouseDown={(e) => { e.preventDefault(); fixarPlaca(v.placa); }}
                                            style={{
                                                padding: "8px 12px", cursor: "pointer", fontSize: "13px",
                                                display: "flex", flexDirection: "column", gap: "2px",
                                                borderBottom: "1px solid #f2f2f2",
                                            }}
                                            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f8fc")}
                                            onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
                                        >
                                            <span style={{ fontWeight: 600, color: "#1F467E" }}>{v.placa}</span>
                                            <span style={{ fontSize: "12px", color: "#777" }}>
                                                {[v.nomeLocatario, [v.marca, v.modelo].filter(Boolean).join(" ")].filter(Boolean).join(" · ") || "Sem locatário"}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        {ultimaConsulta && (
                            <span style={{ fontSize: "12px", color: "#888" }}>
                                Atualizado: {ultimaConsulta.toLocaleTimeString("pt-BR")}
                            </span>
                        )}
                        <button
                            onClick={carregarVeiculos}
                            disabled={carregando}
                            style={{
                                padding: "6px 14px", border: "1px solid #1F467E", borderRadius: "6px",
                                background: "white", color: "#1F467E", cursor: "pointer",
                                display: "flex", alignItems: "center", gap: "6px", fontSize: "13px",
                            }}
                        >
                            <FaSyncAlt size={12} className={carregando ? "animate-spin" : ""} />
                            Atualizar
                        </button>
                    </div>
                </div>

                {/* Placas fixadas (chips) — linha fina que só aparece quando há filtro ativo */}
                {placasFixadas.size > 0 && (
                    <div style={{
                        padding: "6px 20px", background: "white", borderBottom: "1px solid #eee",
                        display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center",
                    }}>
                        {Array.from(placasFixadas).sort().map((placa) => (
                            <span key={placa} style={{
                                display: "inline-flex", alignItems: "center", gap: "6px",
                                background: "#EBF0F7", color: "#1F467E", padding: "4px 8px 4px 10px",
                                borderRadius: "14px", fontSize: "12px", fontWeight: 600,
                            }}>
                                {placa}
                                <button
                                    onClick={() => removerPlaca(placa)}
                                    aria-label={`Remover ${placa}`}
                                    style={{
                                        border: "none", background: "#cdd9ec", color: "#1F467E",
                                        borderRadius: "50%", width: "16px", height: "16px", cursor: "pointer",
                                        lineHeight: "14px", fontSize: "12px", padding: 0,
                                    }}
                                >×</button>
                            </span>
                        ))}
                        <button
                            onClick={() => setPlacasFixadas(new Set())}
                            style={{
                                border: "1px solid #d5d9e0", background: "white", color: "#666",
                                borderRadius: "8px", padding: "4px 10px", fontSize: "12px", cursor: "pointer",
                            }}
                        >Ver todos</button>
                    </div>
                )}

                {/* Mapa (ocupa todo o espaço; a legenda flutua por cima) */}
                <div style={{ flex: 1, position: "relative" }}>
                    {/* Legenda — flutuando no canto inferior esquerdo */}
                    <div style={{
                        position: "absolute", bottom: "16px", left: "10px", zIndex: 1200,
                        background: "rgba(255,255,255,0.92)", borderRadius: "8px", padding: "6px 12px",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
                        display: "flex", gap: "14px", fontSize: "12px", color: "#666", flexWrap: "wrap",
                    }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#4CAF50", display: "inline-block" }} /> Em movimento
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#FFC107", display: "inline-block" }} /> Ligado (parado)
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#9E9E9E", display: "inline-block" }} /> Desligado
                        </span>
                    </div>

                    {/* Barra fluida no topo: aparece sempre que está buscando (inclusive refresh de 60s) */}
                    {carregando && (
                        <div style={{
                            position: "absolute", top: 0, left: 0, right: 0, height: "3px",
                            background: "#dbe4f2", zIndex: 1350, overflow: "hidden",
                        }}>
                            <div style={{
                                position: "absolute", top: 0, height: "100%", background: "#1F467E",
                                borderRadius: "2px", animation: "mapaBarraIndeterminada 1.15s ease-in-out infinite",
                            }} />
                        </div>
                    )}

                    {/* Card central com spinner: só na PRIMEIRA carga (ainda sem veículos) */}
                    {carregando && veiculos.length === 0 && (
                        <div style={{
                            position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                            background: "rgba(255,255,255,0.85)", zIndex: 1300,
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <div style={{
                                display: "flex", flexDirection: "column", alignItems: "center", gap: "14px",
                                background: "white", padding: "24px 32px", borderRadius: "12px",
                                boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
                            }}>
                                <div style={{
                                    width: "38px", height: "38px", borderRadius: "50%",
                                    border: "4px solid #e3eaf5", borderTopColor: "#1F467E",
                                    animation: "mapaGirar 0.8s linear infinite",
                                }} />
                                <span style={{ color: "#1F467E", fontSize: "14px", fontWeight: 500 }}>
                                    Carregando veículos...
                                </span>
                            </div>
                        </div>
                    )}

                    {!carregando && veiculos.length === 0 && (
                        <div style={{
                            position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexDirection: "column", gap: "8px",
                        }}>
                            <span style={{ fontSize: "16px", color: "#666" }}>Nenhum veículo rastreado encontrado</span>
                            <span style={{ fontSize: "13px", color: "#999" }}>Configure a integração com a Agillock em Minha Conta</span>
                        </div>
                    )}

                    <MapContainer
                        center={[-15.78, -47.93]}
                        zoom={4}
                        style={{ height: "100%", width: "100%" }}
                        zoomControl={false}
                    >
                        {/* Zoom no canto superior direito para não colidir com a busca flutuante à esquerda */}
                        <ZoomControl position="topright" />
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />

                        {veiculosExibidos.length > 0 && <FitBounds veiculos={veiculosExibidos} chaveFit={chaveFit} />}

                        {veiculosExibidos.map((v, i) => (
                            <Marker
                                key={`${v.placa}-${i}`}
                                position={[v.latitude, v.longitude]}
                                icon={createVehicleIcon(v.ignition, v.velocidade, v.tipoVeiculo)}
                                eventHandlers={{
                                    popupopen: () => buscarEndereco(v.placa, v.latitude, v.longitude),
                                }}
                            >
                                <Popup>
                                    <div style={{ minWidth: "200px", maxWidth: "90vw", fontSize: "13px" }}>
                                        <div style={{
                                            fontWeight: 700, fontSize: "15px", color: "#1F467E",
                                            borderBottom: "1px solid #eee", paddingBottom: "6px", marginBottom: "6px",
                                        }}>
                                            {v.placa}
                                        </div>
                                        {(v.marca || v.modelo) && (
                                            <div style={{ color: "#666", marginBottom: "4px" }}>
                                                {v.marca} {v.modelo}
                                            </div>
                                        )}
                                        {v.nomeLocatario && (
                                            <div style={{ color: "#1F467E", fontSize: "12px", marginBottom: "8px", fontWeight: 500 }}>
                                                Locatário: {v.nomeLocatario}
                                            </div>
                                        )}
                                        <table style={{ width: "100%", fontSize: "12px" }}>
                                            <tbody>
                                                <tr>
                                                    <td style={{ color: "#888", paddingRight: "8px" }}>Velocidade</td>
                                                    <td style={{ fontWeight: 600 }}>{v.velocidade} km/h</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ color: "#888" }}>Hodômetro</td>
                                                    <td style={{ fontWeight: 600 }}>{v.odometroKm.toLocaleString("pt-BR")} km</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ color: "#888" }}>Ignição</td>
                                                    <td style={{ fontWeight: 600, color: v.ignition ? "#4CAF50" : "#e53935" }}>
                                                        {v.ignition ? "Ligada" : "Desligada"}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ color: "#888" }}>Atualização</td>
                                                    <td>{formatarData(v.ultimaAtualizacao)}</td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        {/* Endereço */}
                                        <div style={{
                                            marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #eee",
                                            display: "flex", alignItems: "flex-start", gap: "6px",
                                        }}>
                                            <FaMapMarkerAlt size={12} color="#e53935" style={{ marginTop: "2px", flexShrink: 0 }} />
                                            <span style={{ fontSize: "12px", color: "#555" }}>
                                                {carregandoEndereco[v.placa] ? "Buscando endereço..." : enderecos[v.placa] || "Carregando..."}
                                            </span>
                                        </div>

                                        {/* Botões de ação */}
                                        <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                                            <button
                                                onClick={() => window.open(`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${v.latitude},${v.longitude}`, '_blank')}
                                                style={{
                                                    flex: 1, padding: "8px", minHeight: "44px",
                                                    border: "1px solid #F9A825", borderRadius: "6px",
                                                    background: "white", color: "#F9A825", cursor: "pointer",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    gap: "6px", fontSize: "13px", fontWeight: 600,
                                                }}
                                            >
                                                <FaStreetView size={14} />
                                                Ver Rua
                                            </button>
                                            <button
                                                onClick={() => bloquearDesbloquearVeiculo(v.placa)}
                                                style={{
                                                    flex: 1, padding: "8px", minHeight: "44px",
                                                    border: "1px solid #1F467E", borderRadius: "6px",
                                                    background: "white", color: "#1F467E", cursor: "pointer",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    gap: "6px", fontSize: "13px", fontWeight: 600,
                                                }}
                                            >
                                                <FaLock size={12} />
                                                Bloquear
                                            </button>
                                        </div>
                                    </div>
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                </div>
                </>
                )}
            </div>
        </LocaSmartLayout>
    );
}

export default MapaVeiculos;
